import type { FulfillmentOrderSummaryRoot, SalesHistoryDateGroupRoot } from '@repo/sdk'

export interface FulfillmentOrderParcel {
  parcelKey: string
  /** First entry is the most recent fulfillment order of the parcel (the list is newest first). */
  fulfillmentOrders: FulfillmentOrderSummaryRoot[]
}

export interface DateParcelGroup {
  date: string
  fulfillmentOrderCount: number
  parcels: FulfillmentOrderParcel[]
}

/**
 * Re-groups the sales-history page (date → broadcast → fulfillment orders) into date → **parcel**
 * (택배 1개, #76). The server keeps sending broadcast groups for older frontends; this screen now
 * groups by the server's `combinedShippingGroupKey` instead, so a parcel that carries purchases from
 * several broadcasts is one row group rather than one per broadcast.
 *
 * A parcel is listed under the date of its most recent fulfillment order, even when older members
 * were bought on an earlier day, so it never appears twice. A server without the key yields one
 * parcel per fulfillment order — the rule is never rebuilt here.
 *
 * The date's count is the server's `fulfillmentOrderCount` — that day's total for the tab, also
 * when the day continues on another page. It is not recounted from the rows on this page
 * (개발항목 220).
 */
export function groupDateGroupsByParcel(
  dateGroups: readonly SalesHistoryDateGroupRoot[],
): DateParcelGroup[] {
  const parcelsByKey = new Map<string, FulfillmentOrderParcel>()
  const result: DateParcelGroup[] = []

  for (const dateGroup of dateGroups) {
    const bucket: DateParcelGroup = {
      date: dateGroup.date,
      fulfillmentOrderCount: dateGroup.fulfillmentOrderCount,
      parcels: [],
    }
    for (const broadcastGroup of dateGroup.broadcastGroups) {
      for (const fulfillmentOrder of broadcastGroup.fulfillmentOrders) {
        const parcelKey =
          fulfillmentOrder.combinedShippingGroupKey ?? `fo:${fulfillmentOrder.fulfillmentOrderId}`
        const existing = parcelsByKey.get(parcelKey)
        if (existing) {
          existing.fulfillmentOrders.push(fulfillmentOrder)
          continue
        }
        const parcel = { parcelKey, fulfillmentOrders: [fulfillmentOrder] }
        parcelsByKey.set(parcelKey, parcel)
        bucket.parcels.push(parcel)
      }
    }
    result.push(bucket)
  }

  return result.filter((bucket) => bucket.parcels.length > 0)
}

/** Distinct broadcast titles of a parcel, or `null` when it holds only marketplace orders. */
export function formatParcelBroadcastTitles(parcel: FulfillmentOrderParcel): string | null {
  const titles = [
    ...new Set(
      parcel.fulfillmentOrders
        .map((fulfillmentOrder) => fulfillmentOrder.broadcast?.title?.trim() ?? '')
        .filter((title) => title !== ''),
    ),
  ]
  return titles.length > 0 ? titles.join(', ') : null
}
